import { featureCollection } from '@turf/turf';
import { useContext, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { useLatest } from 'react-use';
import ImportKmlButton from './ImportKmlButton';
import { generateGeojsonFeatureId } from '../../../utils/layers';
import { MapContext } from '../../../contexts/MapContext';
import { GEOMETRY_TYPES, mapModes, userLayers } from '../../../constants/map';
import LineSegmentIcon from '../../icons/LineSegmentIcon';
import PolygonIcon from '../../icons/PolygonIcon';
import ReplaceFeaturesModal from './ReplaceFeaturesModal';
import parsedKmlToFeatures from '../../../utils/parsedKmlToFeatures';

const getValidGeometryTypes = (geometryType) =>
  geometryType === GEOMETRY_TYPES.LINE
    ? ['LineString', 'Polygon']
    : geometryType === GEOMETRY_TYPES.POLYGON
    ? ['Polygon']
    : null;

const ImportLayerButton = () => {
  const {
    mapState: { activeLayerId, setMapMode, mapMode },
  } = useContext(MapContext);

  const mapModeRef = useLatest(mapMode);

  const { geometryType } = userLayers[activeLayerId] || {};

  const [messages, setMessages] = useState({});
  const [pendingFeatures, setPendingFeatures] = useState(featureCollection([]));

  const onParseKml = (parsedKml) => {
    // We cannot be in edit mode for this, since mapbox-draw will not update its
    // state with the new features, causing subsequent edits to overwrite the
    // imported features.
    if (
      [mapModes.EDIT_LAYER_FEATURE, mapModes.EDIT_LAYER].includes(
        mapModeRef.current,
      )
    ) {
      setMapMode(mapModes.LAYER_ENGAGED, activeLayerId);
    }

    const {
      features: rawFeatures,
      info,
      warnings,
      errors,
    } = parsedKmlToFeatures(parsedKml, getValidGeometryTypes(geometryType));
    const validFeatures = rawFeatures.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        id: generateGeojsonFeatureId(activeLayerId),
      },
    }));

    // Only display messages if there is at least one error or warning
    if (warnings.length + errors.length) {
      setMessages({
        info,
        warning: warnings.join('\n'),
        error: errors.join('\n'),
      });
    }

    setPendingFeatures(featureCollection(validFeatures));
  };

  return (
    <>
      <ImportKmlButton
        onParse={onParseKml}
        messages={messages}
        setMessages={setMessages}
        size="sm"
        outline
        className="mr-2"
        id="import-kml-button"
      >
        {geometryType === GEOMETRY_TYPES.LINE && (
          <>
            <LineSegmentIcon className="mr-2" />
            <UncontrolledTooltip target="import-kml-button">
              Upload lines
            </UncontrolledTooltip>
          </>
        )}
        {geometryType === GEOMETRY_TYPES.POLYGON && (
          <>
            <PolygonIcon className="mr-2" />
            <UncontrolledTooltip target="import-kml-button">
              Upload polygons
            </UncontrolledTooltip>
          </>
        )}
        Import KML
      </ImportKmlButton>
      <ReplaceFeaturesModal
        pendingFeatures={pendingFeatures}
        setPendingFeatures={setPendingFeatures}
      />
    </>
  );
};

export default ImportLayerButton;
